import { Dispatch } from 'redux';
import { businessService } from '../../services/business.service';
import {
  ADD_BUSINESSES,
  CREATE_BUSINESS_FAILED,
  CREATE_BUSINESS_SUCCESS,
  CREATING_BUSINESS,
  CLEAR_CREATE_BUSINESS_ERROR,
  CreateBusinessTypes,
  FETCHING_BUSINESSES,
  FETCH_BUSINESSES_FAILED,
  FetchBusinessesTypes,
  GETTING_BUSINESS,
  GETTING_BUSINESS_SUB_BUSINESSES,
  GET_BUSINESS_FAILED,
  GET_BUSINESS_SUB_BUSINESSES_FAILED,
  GET_BUSINESS_SUB_BUSINESSES_SUCCESS,
  GET_BUSINESS_SUCCESS,
  CLEAR_BUSINESS_ERROR,
  GetBusinessSubBusinessesTypes,
  GetBusinessTypes,
  UPDATE_BUSINESS_FAILED,
  UPDATE_BUSINESS_SUCCESS,
  UPDATE_SECRET_FAILED,
  UPDATE_SECRET_SUCCESS,
  UPDATING_BUSINESS,
  UPDATING_SECRET,
  UpdateBusinessTypes,
  UpdateSecretTypes,
} from '../config/ActionTypes';
import {
  Business,
  BusinessRequest,
  ClientSecretRequest,
  SubBusiness,
  UpdateBusinessRequest,
} from '../config/types';

// Helper functions for error handling
const normalizeError = (error: any): string => {
  if (!error) return 'Ocurrió un error';
  if (typeof error === 'string') return error;
  if (error.originalMessage && typeof error.originalMessage === 'string') return error.originalMessage;
  if (error.userMessage && typeof error.userMessage === 'string') return error.userMessage;
  if (error.message && typeof error.message === 'string') return error.message;
  try {
    return JSON.stringify(error);
  } catch (ex) {
    return String(error);
  }
};

const extractErrorCode = (error: any): number | undefined => {
  if (error && (error as any).errorCode) {
    return (error as any).errorCode;
  } else if (error && (error as any).parsedBody && (error as any).parsedBody.errorCode) {
    return (error as any).parsedBody.errorCode;
  }
  return undefined;
};

//#region Fetch businesses

export const fetchBusinesses = () => {
  return (dispatch: Dispatch<FetchBusinessesTypes>) => {
    dispatch(loadingBusinesses());

    return businessService.fetchBusinesses().then(
      (response) => {
        dispatch(addBusinesses(response!));
      },
      (error) => {
        dispatch(fetchBusinessesFailed(error));
      },
    );
  };
};

export const loadingBusinesses = (): FetchBusinessesTypes => ({
  type: FETCHING_BUSINESSES,
});

export const addBusinesses = (businesses: Business[]): FetchBusinessesTypes => ({
  type: ADD_BUSINESSES,
  businesses,
});

export const fetchBusinessesFailed = (error: string): FetchBusinessesTypes => ({
  type: FETCH_BUSINESSES_FAILED,
  error,
});

//#endregion

//#region Create business

export const createBusiness = (business: BusinessRequest, picture: File) => {
  return (dispatch: Dispatch<CreateBusinessTypes>) => {
    dispatch(creatingBusiness());

    console.log('action_creators/createBusiness: calling service with', business, picture && picture.name);
    return businessService.createBusiness(business, picture).then(
      (response) => {
        console.log('action_creators/createBusiness: success', response);
        dispatch(createBusinessSuccess(response!));
      },
      (error) => {
        console.error('action_creators/createBusiness: error', error);
        const normalizedError = normalizeError(error);
        const errorCode = extractErrorCode(error);
        dispatch(createBusinessFailed(normalizedError, errorCode));
      },
    );
  };
};

export const creatingBusiness = (): CreateBusinessTypes => ({
  type: CREATING_BUSINESS,
});

export const createBusinessSuccess = (business: Business): CreateBusinessTypes => ({
  type: CREATE_BUSINESS_SUCCESS,
  business,
});

export const createBusinessFailed = (error: string, errorCode?: number): CreateBusinessTypes => ({
  type: CREATE_BUSINESS_FAILED,
  error,
  errorCode,
});

export const clearCreateBusinessError = (): CreateBusinessTypes => ({
  type: CLEAR_CREATE_BUSINESS_ERROR,
});

//#endregion

//#region Update business

export const updateBusiness = (updateBusinessRequest: UpdateBusinessRequest) => {
  return (dispatch: Dispatch<UpdateBusinessTypes>) => {
    dispatch(updatingBusiness());

    return businessService.updateBusiness(updateBusinessRequest).then(
      (response) => {
        dispatch(updateBusinessSuccess(response!));
      },
      (error) => {
        dispatch(updateBusinessFailed(error));
      },
    );
  };
};

export const updatingBusiness = (): UpdateBusinessTypes => ({
  type: UPDATING_BUSINESS,
});

export const updateBusinessSuccess = (business: Business): UpdateBusinessTypes => ({
  type: UPDATE_BUSINESS_SUCCESS,
  business,
});

export const updateBusinessFailed = (error: string): UpdateBusinessTypes => ({
  type: UPDATE_BUSINESS_FAILED,
  error,
});

//#endregion

//#region Get business

export const getBusiness = (businessUrlName: string) => {
  return (dispatch: Dispatch<GetBusinessTypes>) => {
    dispatch(gettingBusiness());

    return businessService.getBusiness(businessUrlName).then(
      (response) => {
        dispatch(getBusinessSuccess(response!));
      },
      (error) => {
        // Extract errorCode from error object
        let errorCode: number | undefined;
        try {
          if (error && (error as any).errorCode) {
            errorCode = (error as any).errorCode;
          } else if (error && (error as any).parsedBody && (error as any).parsedBody.errorCode) {
            errorCode = (error as any).parsedBody.errorCode;
          }
        } catch (ex) {
          // no errorCode available
        }
        
        dispatch(getBusinessFailed(error, errorCode));
      },
    );
  };
};

export const gettingBusiness = (): GetBusinessTypes => ({
  type: GETTING_BUSINESS,
});

export const getBusinessSuccess = (business: Business): GetBusinessTypes => ({
  type: GET_BUSINESS_SUCCESS,
  business,
});

export const getBusinessFailed = (error: string, errorCode?: number): GetBusinessTypes => ({
  type: GET_BUSINESS_FAILED,
  error,
  errorCode,
});

export const clearBusinessError = (): GetBusinessTypes => ({
  type: CLEAR_BUSINESS_ERROR,
});

//#region Get businessSubBusinesses

export const getBusinessSubBusinesses = (businessId: number) => {
  return (dispatch: Dispatch<GetBusinessSubBusinessesTypes>) => {
    dispatch(gettingBusinessSubBusinesses());

    return businessService.getBusinessSubBusinesses(businessId).then(
      (response) => {
        dispatch(getBusinessSubBusinessesSuccess(response!));
      },
      (error) => {
        dispatch(getBusinessSubBusinessesFailed(error));
      },
    );
  };
};

export const gettingBusinessSubBusinesses = (): GetBusinessSubBusinessesTypes => ({
  type: GETTING_BUSINESS_SUB_BUSINESSES,
});

export const getBusinessSubBusinessesSuccess = (
  subBusinesses: SubBusiness[],
): GetBusinessSubBusinessesTypes => ({
  type: GET_BUSINESS_SUB_BUSINESSES_SUCCESS,
  subBusinesses,
});

export const getBusinessSubBusinessesFailed = (error: string): GetBusinessSubBusinessesTypes => ({
  type: GET_BUSINESS_SUB_BUSINESSES_FAILED,
  error,
});

//#endregion

//#region Update client secret

export const updateClientSecret = (clientSecretRequest: ClientSecretRequest) => {
  return (dispatch: Dispatch<UpdateSecretTypes>) => {
    dispatch(updatingSecret());

    return businessService.updateClientSecret(clientSecretRequest).then(
      (response) => {
        dispatch(updateSecretSuccess());
      },
      (error) => {
        dispatch(updateSecretFailed(error));
      },
    );
  };
};

export const updatingSecret = (): UpdateSecretTypes => ({
  type: UPDATING_SECRET,
});

export const updateSecretSuccess = (): UpdateSecretTypes => ({
  type: UPDATE_SECRET_SUCCESS,
});

export const updateSecretFailed = (error: string): UpdateSecretTypes => ({
  type: UPDATE_SECRET_FAILED,
  error,
});

//#endregion
